import { isoDate } from "@core/foundation/date";
import {
  OrderKeySchema,
  createModel,
  createModelId,
  createModelSchemas,
  generateOrderKey,
  noModelUpdate,
  recoverOrderKeyFromId,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { ID_PREFIX } from "./shared/id-prefix.ts";

const HealthProbeStatusSchema = z.enum(["failing", "healthy", "warning"]);

export type HealthProbeStatus = z.infer<typeof HealthProbeStatusSchema>;

const HealthProbeMetricSchema = z.object(
  { name: z.string(), unit: z.string(), value: z.number() },
  {
    name: "The name of the measured figure.",
    unit: "The unit the value is measured in.",
    value: "The measured value.",
  },
);

export type HealthProbeMetric = z.infer<typeof HealthProbeMetricSchema>;

const { generateId: generateHealthProbeId, idSchema: HealthProbeIdSchema } =
  createModelId({ idPrefix: ID_PREFIX.HEALTH_PROBE });

export const HealthProbe = createModel({
  create: item => {
    return {
      ...item,
      createdAt: isoDate(),
      id: generateHealthProbeId(),
      orderKey: generateOrderKey(),
    };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        createdAt: z.iso.datetime(),
        id: HealthProbeIdSchema,
        metrics: z.array(HealthProbeMetricSchema).optional(),
        orderKey: OrderKeySchema,
        status: HealthProbeStatusSchema,
        target: z.safeId(),
      },
      {
        createdAt: "When the probe ran, as an ISO 8601 timestamp.",
        id: "The unique identifier for the probe.",
        metrics: "The figures the probe measured, latency included.",
        orderKey: "The key ordering the probes by creation, and nothing else.",
        status: "The probe's status: healthy, warning, or failing.",
        target: "The service or system the probe checks.",
      },
    ),
    derive: {
      create: schema =>
        schema.omit({ createdAt: true, id: true, orderKey: true }),
      // oxlint-disable-next-line no-warning-comments
      // TODO(APT-2405): drop once the stored records carry their orderKey.
      recovery: recoverOrderKeyFromId,
      update: noModelUpdate,
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface HealthProbe extends z.infer<typeof HealthProbe.schema> {}
